import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/utils/store';
import { formatCurrency } from '@/utils/format';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import CurrencyInput from '@/components/ui/CurrencyInput';
import MemberMultiSelect from '@/components/ui/MemberMultiSelect';
import { useFormDraft } from '@/hooks/useFormDraft';
import { Plus, Trash2, Repeat, CircleDot, CalendarDays } from 'lucide-react';

const ICON_OPTIONS = ['🇺🇸', '🏠', '💼', '🏪', '📈', '🚗'];
const COLOR_OPTIONS = ['#3b82f6', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899', '#f97316'];
const CURRENCY_OPTIONS = ['CLP', 'USD', 'EUR'];

export default function AddIncomeUnitModal({ open, onClose, unit = null }) {
  const { t } = useTranslation();
  const addIncomeUnit = useStore((s) => s.addIncomeUnit);
  const updateIncomeUnit = useStore((s) => s.updateIncomeUnit);
  const isEdit = !!unit;
  const draft = useFormDraft('addIncomeUnit');

  const [name, setName] = useState('');
  const [icon, setIcon] = useState(ICON_OPTIONS[0]);
  const [grossIncome, setGrossIncome] = useState('');
  const [currency, setCurrency] = useState('CLP');
  const [color, setColor] = useState(COLOR_OPTIONS[0]);
  const [memberIds, setMemberIds] = useState([]);
  const [recurrence, setRecurrence] = useState('monthly');
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [fixedCosts, setFixedCosts] = useState([]);
  const [newCostName, setNewCostName] = useState('');
  const [newCostAmount, setNewCostAmount] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    if (unit) {
      setName(unit.name || '');
      setIcon(unit.icon || ICON_OPTIONS[0]);
      setGrossIncome(String(unit.grossIncome || ''));
      setCurrency(unit.currency || 'CLP');
      setColor(unit.color || COLOR_OPTIONS[0]);
      setMemberIds(unit.memberIds || []);
      setRecurrence(unit.recurrence || 'monthly');
      setMonth(unit.month || new Date().toISOString().slice(0, 7));
      setFixedCosts(unit.fixedCosts || []);
      setErrors({});
    } else {
      const saved = draft.load();
      if (saved) {
        setName(saved.name || '');
        setIcon(saved.icon || ICON_OPTIONS[0]);
        setGrossIncome(saved.grossIncome || '');
        setCurrency(saved.currency || 'CLP');
        setColor(saved.color || COLOR_OPTIONS[0]);
        setMemberIds(saved.memberIds || []);
        setRecurrence(saved.recurrence || 'monthly');
        setMonth(saved.month || new Date().toISOString().slice(0, 7));
        setFixedCosts(saved.fixedCosts || []);
      }
      setErrors({});
    }
  }, [unit, open]);

  const validate = () => {
    const errs = {};
    if (!name.trim()) errs.name = t('incomeUnits.form.nameRequired');
    if (!grossIncome || Number(grossIncome) <= 0) errs.grossIncome = t('incomeUnits.form.grossRequired');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleAddCost = () => {
    if (!newCostName.trim() || !newCostAmount || Number(newCostAmount) <= 0) return;
    setFixedCosts([...fixedCosts, { id: crypto.randomUUID(), name: newCostName.trim(), amount: Number(newCostAmount) }]);
    setNewCostName('');
    setNewCostAmount('');
  };

  const handleRemoveCost = (costId) => {
    setFixedCosts(fixedCosts.filter((c) => c.id !== costId));
  };

  const handleSave = () => {
    if (!validate()) return;
    const data = {
      name: name.trim(),
      icon,
      grossIncome: Number(grossIncome),
      currency,
      color,
      memberIds,
      recurrence,
      month,
      fixedCosts,
    };
    if (isEdit) {
      updateIncomeUnit(unit.id, data);
    } else {
      addIncomeUnit(data);
    }
    draft.clear();
    handleClose();
  };

  const handleClose = () => {
    setName('');
    setIcon(ICON_OPTIONS[0]);
    setGrossIncome('');
    setCurrency('CLP');
    setColor(COLOR_OPTIONS[0]);
    setMemberIds([]);
    setRecurrence('monthly');
    setMonth(new Date().toISOString().slice(0, 7));
    setFixedCosts([]);
    setNewCostName('');
    setNewCostAmount('');
    setErrors({});
    onClose();
  };

  // Auto-save draft
  useEffect(() => {
    if (!open || isEdit) return;
    draft.save({ name, icon, grossIncome, currency, color, memberIds, recurrence, month, fixedCosts });
  }, [open, isEdit, name, icon, grossIncome, currency, color, memberIds, recurrence, month, fixedCosts]);

  const totalFixed = fixedCosts.reduce((s, c) => s + c.amount, 0);
  const gross = Number(grossIncome) || 0;
  const net = gross - totalFixed;
  const ratio = gross > 0 ? Math.round((net / gross) * 100) : 0;

  return (
    <Modal open={open} onClose={handleClose} title={isEdit ? t('incomeUnits.editUnit') : t('incomeUnits.addNew')} footer={
      <Button className="w-full" onClick={handleSave}>
        {t('common.save')}
      </Button>
    }>
      <div className="space-y-4">
        {/* Name */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('incomeUnits.form.name')}
          </label>
          <input
            type="text"
            className={`input ${errors.name ? 'border-danger-500' : ''}`}
            placeholder={t('incomeUnits.form.namePlaceholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {errors.name && <p className="text-xs text-danger-500 mt-1">{errors.name}</p>}
        </div>

        {/* Icon picker */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('incomeUnits.form.icon')}
          </label>
          <div className="flex gap-2">
            {ICON_OPTIONS.map((ic) => (
              <button
                key={ic}
                onClick={() => setIcon(ic)}
                className={`w-10 h-10 rounded-xl text-lg flex items-center justify-center border-2 transition-all ${
                  icon === ic
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10'
                    : 'border-gray-200 dark:border-gray-700 hover:border-brand-400'
                }`}
              >
                {ic}
              </button>
            ))}
          </div>
        </div>

        {/* Recurrence toggle */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('incomeUnits.recurrenceLabel')}
          </label>
          <div className="flex gap-1.5 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl">
            {['weekly', 'monthly', 'one_time'].map((r) => (
              <button
                key={r}
                onClick={() => setRecurrence(r)}
                className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-medium transition-colors ${
                  recurrence === r ? 'bg-brand-600 text-white' : 'text-gray-500'
                }`}
              >
                {r === 'weekly' && <CalendarDays size={13} />}
                {r === 'monthly' && <Repeat size={13} />}
                {r === 'one_time' && <CircleDot size={13} />}
                {t(`incomeUnits.${r === 'one_time' ? 'oneTime' : r}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Month selector */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('incomeUnits.monthLabel')}
          </label>
          <input
            type="month"
            className="input"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
        </div>

        {/* Gross income + currency */}
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
              {t('incomeUnits.grossIncome')}
            </label>
            <CurrencyInput
              className={`input ${errors.grossIncome ? 'border-danger-500' : ''}`}
              placeholder="0"
              value={grossIncome}
              onChange={setGrossIncome}
              currencyOverride={currency}
            />
            {errors.grossIncome && <p className="text-xs text-danger-500 mt-1">{errors.grossIncome}</p>}
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
              {t('incomeUnits.currency')}
            </label>
            <select
              className="input"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            >
              {CURRENCY_OPTIONS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Color picker */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('family.form.color')}
          </label>
          <div className="flex gap-2">
            {COLOR_OPTIONS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                className={`w-8 h-8 rounded-full border-2 transition-all ${
                  color === c ? 'border-gray-900 dark:border-white scale-110' : 'border-transparent'
                }`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>

        {/* Member assignment */}
        <MemberMultiSelect
          value={memberIds}
          onChange={setMemberIds}
          label={t('incomeUnits.assignedTo')}
        />

        {/* Fixed costs */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('incomeUnits.fixedCosts')}
          </label>
          {fixedCosts.length > 0 && (
            <div className="space-y-2 mb-3">
              {fixedCosts.map((cost) => (
                <div key={cost.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                  <span className="text-sm text-gray-700 dark:text-gray-300">{cost.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-danger-600 dark:text-danger-400">
                      -{formatCurrency(cost.amount, currency)}
                    </span>
                    <button
                      onClick={() => handleRemoveCost(cost.id)}
                      className="p-1 text-gray-400 hover:text-danger-500 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {fixedCosts.length === 0 && (
            <p className="text-xs text-gray-400 mb-3">{t('incomeUnits.noCosts')}</p>
          )}
          <div className="flex gap-2">
            <input
              type="text"
              className="input flex-1 text-sm"
              placeholder={t('incomeUnits.form.name')}
              value={newCostName}
              onChange={(e) => setNewCostName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddCost()}
            />
            <CurrencyInput
              className="input w-28 text-sm"
              placeholder={t('transactions.form.amount')}
              value={newCostAmount}
              onChange={setNewCostAmount}
              currencyOverride={currency}
            />
            <button
              onClick={handleAddCost}
              className="p-2.5 rounded-xl bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 hover:bg-brand-100 dark:hover:bg-brand-500/20 transition-colors"
            >
              <Plus size={18} />
            </button>
          </div>
        </div>

        {/* Preview */}
        {gross > 0 && (
          <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500 dark:text-gray-400">{t('incomeUnits.grossIncome')}</span>
              <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(gross, currency)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500 dark:text-gray-400">{t('incomeUnits.fixedCosts')}</span>
              <span className="font-medium text-danger-600 dark:text-danger-400">-{formatCurrency(totalFixed, currency)}</span>
            </div>
            <div className="h-px bg-gray-200 dark:bg-gray-700" />
            <div className="flex justify-between text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-300">{t('incomeUnits.netIncome')}</span>
              <span className={`font-bold ${net >= 0 ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400'}`}>
                {formatCurrency(net, currency)}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${
                  ratio > 70 ? 'bg-success-500' : ratio > 50 ? 'bg-warning-500' : 'bg-danger-500'
                }`}
                style={{ width: `${Math.max(0, Math.min(100, ratio))}%` }}
              />
            </div>
            <p className="text-xs text-center text-gray-400">{ratio}% {t('incomeUnits.coverage')}</p>
          </div>
        )}

      </div>
    </Modal>
  );
}
